import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../../services/api";
import "./PaymentsAnalyticsDashboard.css";


/* ============================================================
   CONFIGURATION
   ============================================================ */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

const COURSES_ENDPOINT = "get-active-courses";
const PAYMENT_ANALYTICS_ENDPOINT = "course-payment-analytics";

/* ============================================================
   HELPERS
   ============================================================ */

function formatPercentage(value) {
  const n = Number(value);

  if (
    value === null ||
    value === undefined ||
    Number.isNaN(n) ||
    !Number.isFinite(n)
  ) {
    return "0.00%";
  }

  return `${n.toFixed(2)}%`;
}

function formatNumber(value) {
  const n = Number(value);

  if (
    value === null ||
    value === undefined ||
    Number.isNaN(n) ||
    !Number.isFinite(n)
  ) {
    return "0";
  }

  return new Intl.NumberFormat("en-IN").format(n);
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function csvEscape(value) {
  const stringValue = String(value ?? "");

  return /[",\n]/.test(stringValue)
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue;
}

function sanitizeFilename(value) {
  return String(value || "Course")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function clampPercentage(value) {
  const n = Number(value);

  if (!Number.isFinite(n) || Number.isNaN(n)) {
    return 0;
  }

  return Math.min(100, Math.max(0, n));
}

function getTermMatch(items, termName) {
  return safeArray(items).find(
    (item) => item?.term_name === termName
  );
}

/* ============================================================
   TERM MULTI SELECT
   ============================================================ */

function TermMultiSelect({
  availableTerms,
  selectedTerms,
  onChange,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleOutside(event) {
      if (
        wrapRef.current &&
        !wrapRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, []);

 const toggleTerm = (term) => {
    if (selectedTerms.includes(term)) {
        onChange(
            selectedTerms.filter(
                (item) => item !== term
            )
        );
    } else {
        onChange([
            ...selectedTerms,
            term,
        ]);
    }
};
  const selectAll = () => {
    onChange(availableTerms.slice());
  };

  const clearAll = () => {
    onChange([]);
  };

  let triggerLabel = "Select Payment Terms";

  if (!availableTerms.length) {
    triggerLabel = "No terms available";
  } else if (selectedTerms.length === 1) {
    triggerLabel = selectedTerms[0];
  } else if (selectedTerms.length === 2) {
    triggerLabel = selectedTerms.join(" + ");
  } else if (selectedTerms.length > 2) {
    triggerLabel = `${selectedTerms.length} terms selected`;
  }

  return (
    <div className="cpa-term-select" ref={wrapRef}>
      <button
        type="button"
        className="cpa-term-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span className="cpa-term-trigger-text">
          {triggerLabel}
        </span>

        <span className="cpa-chev" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div
          className="cpa-term-dropdown"
          role="listbox"
          aria-multiselectable="true"
        >
          <div className="cpa-term-dropdown-actions">
            <button
              type="button"
              onClick={selectAll}
              disabled={!availableTerms.length}
            >
              Select All
            </button>

            <button
              type="button"
              onClick={clearAll}
              disabled={!selectedTerms.length}
            >
              Clear All
            </button>
          </div>

          <div className="cpa-term-options">
            {!availableTerms.length && (
              <div className="cpa-term-empty">
                No terms available for this course.
              </div>
            )}

            {availableTerms.map((term) => (
              <label
                className="cpa-term-option"
                key={term}
              >
                <input
                  type="checkbox"
                  checked={selectedTerms.includes(term)}
                  onChange={() => toggleTerm(term)}
                />

                <span>{term}</span>
              </label>
            ))}
          </div>

          <div className="cpa-term-dropdown-footer">
            {selectedTerms.length} term
            {selectedTerms.length === 1 ? "" : "s"} selected
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   SUMMARY CARDS
   ============================================================ */

function SummaryCards({
  overall,
  overallTermSummary,
  selectedTerms,
  isMultipleTerms,
}) {
  const selectedTerm = selectedTerms?.[0];

  const singleTermSummary =
    safeArray(overallTermSummary).find(
      (term) => term?.term_name === selectedTerm
    ) || {};

  const paidStudents = isMultipleTerms
    ? overall?.common_paid_students
    : singleTermSummary?.paid_students;

  const pendingStudents = isMultipleTerms
    ? overall?.common_pending_students
    : singleTermSummary?.pending_students;

  const paymentPercentage = isMultipleTerms
    ? overall?.payment_percentage
    : singleTermSummary?.payment_percentage;

  return (
    <div className="cpa-summary-cards">
      <div className="cpa-summary-card">
        <div className="cpa-sc-icon cpa-icon-neutral">
          <span>REG</span>
        </div>
        <div className="cpa-sc-content">
          <div className="cpa-sc-label">Total Registrations</div>
          <div className="cpa-sc-value">
            {formatNumber(overall?.registered_students)}
          </div>
          <div className="cpa-sc-desc">
            Students registered for the selected course
          </div>
        </div>
      </div>

      <div className="cpa-summary-card cpa-paid cpa-primary-metric">
        <div className="cpa-sc-icon cpa-icon-paid">
          <span>✓</span>
        </div>
        <div className="cpa-sc-content">
          <div className="cpa-sc-label">
            {isMultipleTerms ? "Common Paid" : "Paid"}
          </div>
          <div className="cpa-sc-value">
            {formatNumber(paidStudents)}
          </div>
          <div className="cpa-sc-desc">
            {isMultipleTerms
              ? "Paid across all selected terms"
              : `Students paid for ${selectedTerm || "selected term"}`}
          </div>
        </div>
      </div>

      <div className="cpa-summary-card cpa-pending">
        <div className="cpa-sc-icon cpa-icon-pending">
          <span>!</span>
        </div>
        <div className="cpa-sc-content">
          <div className="cpa-sc-label">
            {isMultipleTerms ? "Common Pending" : "Pending"}
          </div>
          <div className="cpa-sc-value">
            {formatNumber(pendingStudents)}
          </div>
          <div className="cpa-sc-desc">
            {isMultipleTerms
              ? "Not paid across all selected terms"
              : `Not paid for ${selectedTerm || "selected term"}`}
          </div>
        </div>
      </div>

      <div className="cpa-summary-card cpa-pct">
        <div className="cpa-sc-icon cpa-icon-pct">
          <span>%</span>
        </div>
        <div className="cpa-sc-content">
          <div className="cpa-sc-label">Payment %</div>
          <div className="cpa-sc-value">
            {formatPercentage(paymentPercentage)}
          </div>
          <div className="cpa-sc-desc">
            {isMultipleTerms
              ? "Common paid / registered"
              : `${selectedTerm || "Selected term"} paid / registered`}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   OVERALL PROGRESS
   ============================================================ */

function ProgressCard({
  overall,
  isMultipleTerms,
}) {
  const registered =
    Number(overall.registered_students) || 0;

  const paid =
    Number(overall.common_paid_students) || 0;

  const percentage = clampPercentage(
    overall.payment_percentage
  );

  const description = isMultipleTerms
    ? "Common Paid represents students who have valid payment in every selected term."
    : "Common Paid represents students who have valid payment in the selected term.";

  return (
    <div className="cpa-progress-card">
      <div className="cpa-progress-header">
        <div>
          <div className="cpa-section-kicker">
            Overall Status
          </div>

          <div className="cpa-pc-title">
            Overall Payment Progress
          </div>
        </div>

        <div className="cpa-pc-pct">
          {formatPercentage(overall.payment_percentage)}
        </div>
      </div>

      <div className="cpa-pc-stat">
        <strong>{formatNumber(paid)}</strong>
        {" / "}
        <strong>{formatNumber(registered)}</strong>
        {" students paid "}
        {isMultipleTerms
          ? "across selected terms"
          : "in selected term"}
      </div>

      <div
        className="cpa-progress-track"
        aria-label={`Payment progress ${formatPercentage(
          overall.payment_percentage
        )}`}
      >
        <div
          className="cpa-progress-fill"
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="cpa-progress-note">
        <span className="cpa-info-dot">i</span>
        <span>{description}</span>
      </div>
    </div>
  );
}

/* ============================================================
   TERM CARDS
   ============================================================ */

function TermCards({ termSummary }) {
  if (!termSummary.length) {
    return (
      <div className="cpa-term-cards-empty">
        No term-wise data available.
      </div>
    );
  }

  return (
    <div className="cpa-term-cards">
      {termSummary.map((term) => {
        const percentage = clampPercentage(
          term.payment_percentage
        );

        return (
          <div
            className="cpa-term-card"
            key={term.term_name}
          >
            <div className="cpa-tc-header">
              <div className="cpa-tc-name">
                {term.term_name || "—"}
              </div>

              <div className="cpa-tc-percent">
                {formatPercentage(term.payment_percentage)}
              </div>
            </div>

            <div className="cpa-tc-main">
              <div className="cpa-tc-stat">
                <span className="cpa-tc-stat-label">
                  Paid
                </span>

                <strong className="cpa-tc-paid">
                  {formatNumber(term.paid_students)}
                </strong>
              </div>

              <div className="cpa-tc-divider" />

              <div className="cpa-tc-stat">
                <span className="cpa-tc-stat-label">
                  Pending
                </span>

                <strong className="cpa-tc-pending">
                  {formatNumber(term.pending_students)}
                </strong>
              </div>
            </div>

            <div className="cpa-tc-progress">
              <div className="cpa-tc-progress-track">
                <div
                  className="cpa-tc-progress-fill"
                  style={{
                    width: `${percentage}%`,
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   TECHNOLOGY TABLE
   ============================================================ */

function AnalyticsTable({
  terms,
  overall,
  overallTermSummary,
  technologies,
}) {
  const isMultipleTerms = terms.length > 1;
  const selectedTerm = terms[0];

  const getSingleTerm = (items) =>
    getTermMatch(items, selectedTerm) || {};
  return (
    <div className="cpa-table-card">
      <div className="cpa-table-heading">
        <div>
          <div className="cpa-section-kicker">
            Detailed Breakdown
          </div>

          <div className="cpa-section-title">
            Technology / Batch Payment Analytics
          </div>
        </div>

        <div className="cpa-table-caption">
          {terms.length === 1
            ? "Showing selected term"
            : `Showing ${terms.length} selected terms`}
        </div>
      </div>

      <div className="cpa-table-scroll">
        <table className="cpa-analytics-table">
          <thead>
            <tr className="cpa-group-row">
              <th
                rowSpan={2}
                className="cpa-sticky-col cpa-sticky-tech"
              >
                Technology / Batch
              </th>

              <th
                rowSpan={2}
                className="cpa-sticky-col cpa-sticky-reg"
              >
                Registrations
              </th>

              <th
                rowSpan={2}
                className="cpa-sticky-col cpa-sticky-paid"
              >
                {isMultipleTerms ? "Common Paid" : "Paid"}
              </th>

              <th
                rowSpan={2}
                className="cpa-sticky-col cpa-sticky-pending"
              >
                {isMultipleTerms ? "Common Pending" : "Pending"}
              </th>

              <th rowSpan={2}>
                Payment %
              </th>

              <th rowSpan={2}>
                Share %
              </th>

              {terms.map((term) => (
                <th
                  colSpan={4}
                  key={term}
                  className="cpa-term-group-header"
                >
                  {term}
                </th>
              ))}
            </tr>

            <tr className="cpa-sub-header">
              {terms.map((term) => (
                <React.Fragment key={term}>
                  <th>Paid</th>
                  <th>Pending</th>
                  <th>Payment %</th>
                  <th>Share %</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* ================= OVERALL ================= */}
            <tr className="cpa-overall-row">
              <td className="cpa-sticky-col cpa-sticky-tech">
                <div className="cpa-overall-label">
                  <span className="cpa-overall-dot" />
                  Overall
                </div>
              </td>

              <td className="cpa-sticky-col cpa-sticky-reg">
                {formatNumber(
                  overall.registered_students
                )}
              </td>

              <td className="cpa-sticky-col cpa-sticky-paid cpa-cell-paid cpa-strong">
                {formatNumber(
                  isMultipleTerms
                    ? overall.common_paid_students
                    : getSingleTerm(overallTermSummary)?.paid_students
                )}
              </td>

              <td className="cpa-sticky-col cpa-sticky-pending cpa-cell-pending">
                {formatNumber(
                  isMultipleTerms
                    ? overall.common_pending_students
                    : getSingleTerm(overallTermSummary)?.pending_students
                )}
              </td>

              <td className="cpa-cell-pct cpa-strong">
                {formatPercentage(
                  isMultipleTerms
                    ? overall.payment_percentage
                    : getSingleTerm(overallTermSummary)?.payment_percentage
                )}
              </td>

              <td className="cpa-cell-dim">
                100.00%
              </td>

              {/* CRITICAL:
                  Overall term columns come from
                  overall_term_summary
              */}
              {terms.map((term) => {
                const match = getTermMatch(
                  overallTermSummary,
                  term
                );

                if (!match) {
                  return (
                    <React.Fragment key={term}>
                      <td className="cpa-cell-dim">—</td>
                      <td className="cpa-cell-dim">—</td>
                      <td className="cpa-cell-dim">—</td>
                      <td className="cpa-cell-dim">—</td>
                    </React.Fragment>
                  );
                }

                return (
                  <React.Fragment key={term}>
                    <td className="cpa-cell-paid cpa-term-overall-cell">
                      {formatNumber(
                        match.paid_students
                      )}
                    </td>

                    <td className="cpa-cell-pending cpa-term-overall-cell">
                      {formatNumber(
                        match.pending_students
                      )}
                    </td>

                    <td className="cpa-cell-pct cpa-term-overall-cell">
                      {formatPercentage(
                        match.payment_percentage
                      )}
                    </td>

                    <td className="cpa-term-overall-cell">
                      {formatPercentage(
                        match.share_percentage
                      )}
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>

            {/* ================= TECHNOLOGIES ================= */}
            {technologies.map((technology, index) => {
              const techTerms = safeArray(
                technology.terms
              );

              const technologyName =
                technology.technology_name ||
                "Unnamed Technology";

              const initial = technologyName
                .charAt(0)
                .toUpperCase();

              return (
                <tr
                  key={
                    technology.technology_id ||
                    `${technologyName}-${index}`
                  }
                >
                  <td className="cpa-tech-cell cpa-sticky-col cpa-sticky-tech">
                    {technology.technology_icon ? (
                      <span className="cpa-tech-icon">
                        <img
                          src={
                            technology.technology_icon
                          }
                          alt=""
                          onError={(event) => {
                            event.currentTarget.style.display =
                              "none";

                            if (
                              event.currentTarget
                                .parentElement
                            ) {
                              event.currentTarget.parentElement.classList.add(
                                "cpa-tech-icon-fallback"
                              );

                              event.currentTarget.parentElement.setAttribute(
                                "data-initial",
                                initial
                              );
                            }
                          }}
                        />
                      </span>
                    ) : (
                      <span
                        className="cpa-tech-icon cpa-tech-icon-fallback"
                        data-initial={initial}
                      >
                        {initial}
                      </span>
                    )}

                    <span className="cpa-tech-name">
                      {technologyName}
                    </span>
                  </td>

                  <td className="cpa-sticky-col cpa-sticky-reg">
                    {formatNumber(
                      technology.registered_students
                    )}
                  </td>

                  <td className="cpa-sticky-col cpa-sticky-paid cpa-cell-paid cpa-strong">
                    {formatNumber(
                      isMultipleTerms
                        ? technology.common_paid_students
                        : getSingleTerm(techTerms)?.paid_students
                    )}
                  </td>

                  <td className="cpa-sticky-col cpa-sticky-pending cpa-cell-pending">
                    {formatNumber(
                      isMultipleTerms
                        ? technology.common_pending_students
                        : getSingleTerm(techTerms)?.pending_students
                    )}
                  </td>

                  <td className="cpa-cell-pct">
                    <div className="cpa-tech-payment">
                      <span>
                        {formatPercentage(
                          isMultipleTerms
                            ? technology.payment_percentage
                            : getSingleTerm(techTerms)?.payment_percentage
                        )}
                      </span>

                      <div className="cpa-mini-progress">
                        <div
                          style={{
                            width: `${clampPercentage(
                              technology.payment_percentage
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  </td>

                  <td>
                    {formatPercentage(
                      technology.share_percentage
                    )}
                  </td>

                  {terms.map((term) => {
                    const match = getTermMatch(
                      techTerms,
                      term
                    );

                    if (!match) {
                      return (
                        <React.Fragment key={term}>
                          <td className="cpa-cell-dim">—</td>
                          <td className="cpa-cell-dim">—</td>
                          <td className="cpa-cell-dim">—</td>
                          <td className="cpa-cell-dim">—</td>
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment key={term}>
                        <td className="cpa-cell-paid">
                          {formatNumber(
                            match.paid_students
                          )}
                        </td>

                        <td className="cpa-cell-pending">
                          {formatNumber(
                            match.pending_students
                          )}
                        </td>

                        <td className="cpa-cell-pct">
                          {formatPercentage(
                            match.payment_percentage
                          )}
                        </td>

                        <td>
                          {formatPercentage(
                            match.share_percentage
                          )}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {technologies.length === 0 && (
        <div className="cpa-table-empty">
          <div className="cpa-table-empty-icon">
            —
          </div>

          <div>
            <strong>
              No payment analytics available
            </strong>

            <p>
              No technology-level data was returned for
              the selected course and terms.
            </p>
          </div>
        </div>
      )}

      <div className="cpa-table-footnote">
        <span className="cpa-info-dot">i</span>

        <span>
          Values are computed by the backend. {terms.length > 1
            ? "Common Paid represents students with valid payment in every selected term."
            : `Paid and Pending represent the selected ${selectedTerm || "term"}.`}
        </span>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function CoursePaymentAnalytics() {
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] =
    useState(true);
  const [coursesError, setCoursesError] =
    useState("");

  const [selectedCourseId, setSelectedCourseId] =
    useState("");

  const [selectedCourseName, setSelectedCourseName] =
    useState("");

  const [availableTerms, setAvailableTerms] =
    useState([]);

  const [selectedTerms, setSelectedTerms] =
    useState([]);

  const [termsLoading, setTermsLoading] =
    useState(false);

  const [analyticsData, setAnalyticsData] =
    useState(null);

  const [isLoading, setIsLoading] =
    useState(false);

  const [error, setError] = useState("");

  const [validationMsg, setValidationMsg] =
    useState("");

  const [lastUpdated, setLastUpdated] =
    useState(null);

  /* ==========================================================
     LOAD ACTIVE COURSES
     ========================================================== */

  useEffect(() => {
    let cancelled = false;

    async function loadCourses() {
      setCoursesLoading(true);
      setCoursesError("");

      try {
        const response = await api.get(
          `${API_BASE_URL}${COURSES_ENDPOINT}`
        );

        const result = response.data;

        let list = [];

        if (Array.isArray(result)) {
          list = result;
        } else if (Array.isArray(result?.data)) {
          list = result.data;
        } else if (Array.isArray(result?.courses)) {
          list = result.courses;
        } else if (
          Array.isArray(result?.courseRegistrations)
        ) {
          list = result.courseRegistrations;
        }

        const mapped = list
          .map((course) => ({
            id:
              course?._id ||
              course?.course_id ||
              course?.id ||
              "",

            name:
              course?.course_name ||
              course?.name ||
              "Untitled Course",
          }))
          .filter((course) => course.id);

        if (!cancelled) {
          setCourses(mapped);
        }
      } catch (requestError) {
        if (!cancelled) {
          setCoursesError(
            "Unable to load courses. Please refresh and try again."
          );
        }
      } finally {
        if (!cancelled) {
          setCoursesLoading(false);
        }
      }
    }

    loadCourses();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ==========================================================
     DISCOVER TERMS
     ========================================================== */

  const discoverTerms = useCallback(
    async (courseId) => {
      setTermsLoading(true);
      setError("");

      try {
        const response = await api.post(
          `${API_BASE_URL}${PAYMENT_ANALYTICS_ENDPOINT}`,
          {
            course_ids: [courseId],
          }
        );

        const result = response.data;

        if (
          !result ||
          result.success !== true ||
          !Array.isArray(result.data) ||
          result.data.length === 0
        ) {
          throw new Error("no_data");
        }

        const courseData = result.data[0];

        const termSummary = safeArray(
          courseData?.overall_term_summary
        );

        let terms = termSummary
          .map((term) => term?.term_name)
          .filter(Boolean);

        if (!terms.length) {
          const technologies = safeArray(
            courseData?.technologies
          );

          const termSet = new Set();

          technologies.forEach((technology) => {
            safeArray(technology?.terms).forEach(
              (term) => {
                if (term?.term_name) {
                  termSet.add(term.term_name);
                }
              }
            );
          });

          terms = Array.from(termSet);
        }

        setAvailableTerms(terms);
      } catch (requestError) {
        setError(
          "Unable to determine payment terms for this course."
        );

        setAvailableTerms([]);
      } finally {
        setTermsLoading(false);
      }
    },
    []
  );

  /* ==========================================================
     COURSE CHANGE
     ========================================================== */

  const handleCourseChange = (event) => {
    const courseId = event.target.value;

    const selectedOption =
      event.target.options[
        event.target.selectedIndex
      ];

    const courseName =
      selectedOption?.text || "";

    setError("");
    setValidationMsg("");

    setSelectedCourseId(courseId);
    setSelectedCourseName(courseName);

    setSelectedTerms([]);
    setAvailableTerms([]);
    setAnalyticsData(null);
    setLastUpdated(null);

    if (courseId) {
      discoverTerms(courseId);
    }
  };

  /* ==========================================================
     TERMS CHANGE
     ========================================================== */

  const handleTermsChange = (nextTerms) => {
    setSelectedTerms(nextTerms);
    setValidationMsg("");
    setError("");

    // Once the user changes terms, previous analytics
    // no longer represent the current selection.
    setAnalyticsData(null);
    setLastUpdated(null);
  };

  /* ==========================================================
     ANALYTICS REQUEST
     ========================================================== */

  const runAnalyticsRequest = useCallback(async () => {
    if (
      !selectedCourseId ||
      !selectedTerms.length
    ) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await api.post(
        `${API_BASE_URL}${PAYMENT_ANALYTICS_ENDPOINT}`,
        {
          course_ids: [selectedCourseId],
          selected_terms: selectedTerms,
        }
      );

      const result = response.data;

      if (
        !result ||
        result.success !== true ||
        !Array.isArray(result.data) ||
        result.data.length === 0
      ) {
        throw new Error("no_data");
      }

      setAnalyticsData(result.data[0]);
      setLastUpdated(new Date());
    } catch (requestError) {
      let message =
        "Unable to load payment analytics.";

      if (requestError?.response) {
        const status =
          requestError.response.status;

        if (status === 404) {
          message = "Course not found.";
        } else if (status === 400) {
          message =
            "Please check your course and term selection.";
        } else if (status >= 500) {
          message =
            "Unable to load payment analytics.";
        }
      } else if (requestError?.request) {
        message =
          "Network error. Please check your connection.";
      }

      setError(message);
      setAnalyticsData(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCourseId, selectedTerms]);

  /* ==========================================================
     GENERATE
     ========================================================== */

  const handleGenerateClick = () => {
    setValidationMsg("");
    setError("");

    if (!selectedCourseId) {
      setValidationMsg(
        "Please select a course."
      );
      return;
    }

    if (!selectedTerms.length) {
      setValidationMsg(
        "Please select at least one payment term."
      );
      return;
    }

    runAnalyticsRequest();
  };

  /* ==========================================================
     REFRESH
     ========================================================== */

  const handleRefreshClick = () => {
    if (
      !selectedCourseId ||
      !selectedTerms.length
    ) {
      return;
    }

    runAnalyticsRequest();
  };

  /* ==========================================================
     EXPORT CSV
     ========================================================== */

  const handleExportClick = () => {
    if (!analyticsData) {
      return;
    }

    const terms =
      safeArray(analyticsData.selected_terms)
        .length > 0
        ? analyticsData.selected_terms
        : selectedTerms;

    const technologies = safeArray(
      analyticsData.technologies
    );

    const overall =
      analyticsData.overall || {};

    const overallTermSummary = safeArray(
      analyticsData.overall_term_summary
    );

    const headers = [
      "Technology / Batch",
      "Registrations",
      "Common Paid",
      "Common Pending",
      "Payment %",
      "Share %",
    ];

    terms.forEach((term) => {
      headers.push(
        `${term} Paid`,
        `${term} Pending`,
        `${term} Payment %`,
        `${term} Share %`
      );
    });

    const rows = [headers];

    /* ---------------- OVERALL CSV ROW ---------------- */

    const overallRow = [
      "Overall",
      overall.registered_students ?? 0,
      overall.common_paid_students ?? 0,
      overall.common_pending_students ?? 0,
      formatPercentage(
        overall.payment_percentage
      ),
      "—",
    ];

    terms.forEach((term) => {
      const match = getTermMatch(
        overallTermSummary,
        term
      );

      if (!match) {
        overallRow.push(
          "",
          "",
          "",
          ""
        );
      } else {
        overallRow.push(
          match.paid_students ?? 0,
          match.pending_students ?? 0,
          formatPercentage(
            match.payment_percentage
          ),
          formatPercentage(
            match.share_percentage
          )
        );
      }
    });

    rows.push(overallRow);

    /* ---------------- TECHNOLOGY ROWS ---------------- */

    technologies.forEach((technology) => {
      const row = [
        technology.technology_name || "",
        technology.registered_students ?? 0,
        technology.common_paid_students ?? 0,
        technology.common_pending_students ?? 0,
        formatPercentage(
          technology.payment_percentage
        ),
        formatPercentage(
          technology.share_percentage
        ),
      ];

      const technologyTerms = safeArray(
        technology.terms
      );

      terms.forEach((term) => {
        const match = getTermMatch(
          technologyTerms,
          term
        );

        if (!match) {
          row.push(
            "",
            "",
            "",
            ""
          );
        } else {
          row.push(
            match.paid_students ?? 0,
            match.pending_students ?? 0,
            formatPercentage(
              match.payment_percentage
            ),
            formatPercentage(
              match.share_percentage
            )
          );
        }
      });

      rows.push(row);
    });

    const csvContent = rows
      .map((row) =>
        row.map(csvEscape).join(",")
      )
      .join("\n");

    const courseNameSafe = sanitizeFilename(
      analyticsData.course_name ||
        selectedCourseName ||
        "Course"
    );

    const termsSafe = terms
      .map(sanitizeFilename)
      .join("-");

    const filename = `${courseNameSafe}-${termsSafe}-Payment-Analytics.csv`;

    const blob = new Blob(
      [csvContent],
      {
        type: "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const anchor =
      document.createElement("a");

    anchor.href = url;
    anchor.download = filename;

    document.body.appendChild(anchor);

    anchor.click();

    document.body.removeChild(anchor);

    URL.revokeObjectURL(url);
  };

  /* ==========================================================
     DERIVED VALUES
     ========================================================== */

  const terms = useMemo(() => {
    if (!analyticsData) {
      return [];
    }

    const backendTerms = safeArray(
      analyticsData.selected_terms
    );

    return backendTerms.length
      ? backendTerms
      : selectedTerms;
  }, [analyticsData, selectedTerms]);

  const overall = analyticsData
    ? analyticsData.overall || {}
    : {};

  const termSummary = analyticsData
    ? safeArray(
        analyticsData.overall_term_summary
      )
    : [];

  const technologies = analyticsData
    ? safeArray(
        analyticsData.technologies
      )
    : [];

  const generateDisabled =
    isLoading ||
    !selectedCourseId ||
    selectedTerms.length === 0;

  const refreshDisabled =
    isLoading ||
    !analyticsData;

  const isMultipleTerms =
    terms.length > 1;

  /* ==========================================================
     RENDER
     ========================================================== */

  return (
    <div className="cpa-app">
      {/* ======================================================
          HEADER
          ====================================================== */}

      <header className="cpa-masthead">
        <div className="cpa-brand-block">
          <div className="cpa-eyebrow">
            PAYMENT REPORTING
          </div>

          <h1>
            Course Payment Analytics
          </h1>

          <p className="cpa-sub">
            {analyticsData
              ? "Course payment completion overview"
              : selectedCourseId
              ? "Select payment terms, then generate analytics."
              : "Select a course to begin."}
          </p>
        </div>

        {analyticsData && (
          <div className="cpa-course-context">
            <div className="cpa-context-label">
              SELECTED COURSE
            </div>

            <div className="cpa-cname">
              {analyticsData.course_name ||
                selectedCourseName}
            </div>

            <div className="cpa-cmeta">
              {terms.length
                ? terms.join(" + ")
                : "—"}
            </div>

            {analyticsData.course_id && (
              <div className="cpa-cid">
                {analyticsData.course_id}
              </div>
            )}
          </div>
        )}
      </header>

      {/* ======================================================
          CONTROL PANEL
          ====================================================== */}

      <section className="cpa-panel">
        <div className="cpa-panel-header">
          <div>
            <div className="cpa-panel-title">
              Analytics Configuration
            </div>

            <div className="cpa-panel-subtitle">
              Choose the course and payment terms you
              want to review.
            </div>
          </div>

          {selectedTerms.length > 0 && (
            <div className="cpa-selection-count">
              {selectedTerms.length} term
              {selectedTerms.length === 1
                ? ""
                : "s"} selected
            </div>
          )}
        </div>

        <div className="cpa-panel-grid">
          {/* COURSE */}
          <div className="cpa-field cpa-course-field">
            <label htmlFor="cpaCourseSelect">
              Course
            </label>

            <select
              id="cpaCourseSelect"
              className="cpa-control"
              value={selectedCourseId}
              onChange={handleCourseChange}
              disabled={coursesLoading}
            >
              {coursesLoading && (
                <option value="">
                  Loading courses...
                </option>
              )}

              {!coursesLoading &&
                coursesError && (
                  <option value="">
                    Unable to load courses.
                  </option>
                )}

              {!coursesLoading &&
                !coursesError && (
                  <>
                    <option value="">
                      Select Course
                    </option>

                    {courses.map((course) => (
                      <option
                        value={course.id}
                        key={course.id}
                      >
                        {course.name}
                      </option>
                    ))}
                  </>
                )}
            </select>
          </div>

          {/* TERMS */}
          <div className="cpa-field cpa-terms-field">
            <label>
              Payment Terms
            </label>

            <TermMultiSelect
              availableTerms={availableTerms}
              selectedTerms={selectedTerms}
              onChange={handleTermsChange}
              disabled={
                !selectedCourseId ||
                termsLoading
              }
            />

            {termsLoading && (
              <div className="cpa-field-hint">
                Loading available terms...
              </div>
            )}
          </div>

          {/* ACTIONS */}
          <div className="cpa-actions">
            <button
              type="button"
              className="cpa-btn cpa-btn-primary"
              disabled={generateDisabled}
              onClick={handleGenerateClick}
            >
              {isLoading && (
                <span className="cpa-spinner" />
              )}

              <span>
                {isLoading
                  ? "Generating Analytics..."
                  : "Generate Analytics"}
              </span>
            </button>

            <button
              type="button"
              className="cpa-btn cpa-btn-secondary"
              disabled={refreshDisabled}
              onClick={handleRefreshClick}
            >
              <span className="cpa-refresh-icon">
                ↻
              </span>

              <span className="cpa-refresh-text">
                Refresh
              </span>
            </button>

            <button
              type="button"
              className="cpa-btn cpa-btn-ghost"
              disabled={!analyticsData}
              onClick={handleExportClick}
            >
              <span>⇩</span>
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {(validationMsg || coursesError) && (
          <div className="cpa-validation-msg">
            <span className="cpa-banner-icon">
              !
            </span>

            <span>
              {validationMsg || coursesError}
            </span>
          </div>
        )}
      </section>

      {/* ======================================================
          ERROR
          ====================================================== */}

      {error && (
        <div
          className="cpa-banner cpa-banner-error"
          role="alert"
        >
          <span className="cpa-banner-icon">
            !
          </span>

          <span>{error}</span>
        </div>
      )}

      {/* ======================================================
          LOADING
          ====================================================== */}

      {isLoading && (
        <div className="cpa-skeleton-wrap">
          <div className="cpa-sk-cards">
            <div className="cpa-sk-block cpa-sk-card" />
            <div className="cpa-sk-block cpa-sk-card" />
            <div className="cpa-sk-block cpa-sk-card" />
            <div className="cpa-sk-block cpa-sk-card" />
          </div>

          <div className="cpa-sk-term-row">
            <div className="cpa-sk-block cpa-sk-term" />
            <div className="cpa-sk-block cpa-sk-term" />
          </div>

          <div className="cpa-sk-block cpa-sk-table" />
        </div>
      )}

      {/* ======================================================
          EMPTY STATE
          ====================================================== */}

      {!isLoading && !analyticsData && (
        <div className="cpa-empty-state">
          <div className="cpa-es-icon">
            <span>⌁</span>
          </div>

          <div className="cpa-es-title">
            {selectedCourseId
              ? selectedTerms.length
                ? "Ready to generate analytics."
                : "Select payment terms to continue."
              : "Select a course and payment terms to generate analytics."}
          </div>

          <div className="cpa-es-sub">
            {selectedCourseId
              ? selectedTerms.length
                ? "Click Generate Analytics to view the payment summary."
                : "Choose one or more terms from the payment terms selector."
              : "Analytics will appear here once generated."}
          </div>
        </div>
      )}

      {/* ======================================================
          DASHBOARD
          ====================================================== */}

      {!isLoading && analyticsData && (
        <main className="cpa-dashboard-body">
          {/* DASHBOARD CONTEXT */}
          <div className="cpa-dashboard-context">
            <div>
              <div className="cpa-dashboard-course">
                {analyticsData.course_name ||
                  selectedCourseName}
              </div>

              <div className="cpa-dashboard-term-line">
                Selected terms:
                <span>
                  {terms.join(" + ")}
                </span>
              </div>
            </div>

            <div className="cpa-dashboard-meta">
              {lastUpdated && (
                <>
                  Last updated:{" "}
                  {lastUpdated.toLocaleString(
                    "en-IN"
                  )}
                </>
              )}
            </div>
          </div>

          {/* SUMMARY */}
          <SummaryCards
            overall={overall}
            overallTermSummary={termSummary}
            selectedTerms={terms}
            isMultipleTerms={isMultipleTerms}
          />

          {/* TERM-WISE */}
          <section className="cpa-section">
            <div className="cpa-section-header">
              <div>
                <div className="cpa-section-kicker">
                  Individual Terms
                </div>

                <div className="cpa-section-title">
                  Term-Wise Payment Status
                </div>
              </div>

              <span className="cpa-tag cpa-tag-muted">
                Individual term results
              </span>
            </div>

            <TermCards
              termSummary={termSummary}
            />
          </section>

          {/* MAIN TABLE */}
          <AnalyticsTable
            terms={terms}
            overall={overall}
            overallTermSummary={
              termSummary
            }
            technologies={technologies}
          />

          {/* FOOTER */}
          <div className="cpa-meta-bar">
            <span>
              Backend values are displayed without
              frontend recalculation.
            </span>

            <span>
              Course Payment Analytics
            </span>
          </div>
        </main>
      )}
    </div>
  );
}